import { uuidv7 } from 'uuidv7';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '@buildbee/contracts';

// ---------------------------------------------------------------------------
// IDs
// ---------------------------------------------------------------------------

/** New primary key (UUID v7: time-ordered, safe to generate on clients for offline sync). */
export function newId(): string {
  return uuidv7();
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/** Milliseconds timestamp embedded in a UUID v7. */
export function uuidV7Time(id: string): Date | null {
  if (!isUuid(id) || id[14] !== '7') return null;
  const hex = id.replace(/-/g, '').slice(0, 12);
  return new Date(parseInt(hex, 16));
}

/** URL-safe random token (base64url), e.g. for invitations and public signing links. */
export function randomToken(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  let bin = '';
  for (const b of buf) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** SHA-256 hex digest (WebCrypto, works in Workers, browsers and Node 20+). */
export async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ---------------------------------------------------------------------------
// Money (integer cents, basis points)
// ---------------------------------------------------------------------------

/** Round half away from zero to integer cents. */
export function roundCents(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** Apply basis points (1% = 100 bps) to an amount in cents. */
export function applyBps(cents: number, bps: number): number {
  return roundCents((cents * bps) / 10_000);
}

/** Quantities are decimals with at most this many places (the column holds 4; 6 leaves headroom). */
const QTY_SCALE = 1_000_000;
const QTY_SCALE_N = BigInt(QTY_SCALE);

/**
 * `quantity x unitCostCents` rounded half away from zero to whole cents, without binary float
 * drift: `0.285 x 100` is 28.499999999999996 as a float, which `Math.round` takes to 28 instead of
 * 29. The quantity is read as a decimal with up to 6 places and multiplied in integers (BigInt, so
 * large totals stay exact too).
 */
export function lineCostCents(quantity: number, unitCostCents: number): number {
  const scaled = Math.round(quantity * QTY_SCALE);
  if (!Number.isFinite(quantity) || !Number.isSafeInteger(scaled) || !Number.isSafeInteger(unitCostCents)) {
    return roundCents(quantity * unitCostCents);
  }
  const num = BigInt(scaled) * BigInt(unitCostCents);
  const neg = num < 0n;
  const abs = neg ? -num : num;
  let q = abs / QTY_SCALE_N;
  if ((abs % QTY_SCALE_N) * 2n >= QTY_SCALE_N) q += 1n;
  const out = Number(q);
  return neg ? -out : out;
}

export type PricedLine = {
  quantity: number;
  unitCostCents: number;
  markupBps?: number | null;
  taxable?: boolean;
};

/** Cost and sell totals for one line; line markup overrides the document default. */
export function lineTotals(line: PricedLine, defaultMarkupBps = 0) {
  const costTotalCents = lineCostCents(line.quantity, line.unitCostCents);
  const markup = line.markupBps ?? defaultMarkupBps;
  const priceTotalCents = costTotalCents + applyBps(costTotalCents, markup);
  return { costTotalCents, priceTotalCents };
}

/** Subtotal, tax and total for a priced document (estimate, change order). */
export function documentTotals(lines: PricedLine[], opts: { markupBps?: number; taxRateBps?: number } = {}) {
  let costSubtotalCents = 0;
  let subtotalCents = 0;
  let taxableCents = 0;
  for (const line of lines) {
    const t = lineTotals(line, opts.markupBps ?? 0);
    costSubtotalCents += t.costTotalCents;
    subtotalCents += t.priceTotalCents;
    if (line.taxable !== false) taxableCents += t.priceTotalCents;
  }
  const taxCents = applyBps(taxableCents, opts.taxRateBps ?? 0);
  return { costSubtotalCents, subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
}

/** Format integer cents as money. Defaults to Canadian dollars in Canadian English (`$1,234.50`). */
export function formatCents(cents: number, currency: string = DEFAULT_CURRENCY, locale: string = DEFAULT_LOCALE): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

export type MoneySettings = { currency?: string | null; locale?: string | null } | null | undefined;

/** The currency and locale of a workspace's settings (stored sparse), falling back to CAD / en-CA. */
export function moneySettings(settings: MoneySettings): { currency: string; locale: string } {
  return { currency: settings?.currency?.trim().toUpperCase() || DEFAULT_CURRENCY, locale: settings?.locale?.trim() || DEFAULT_LOCALE };
}

/** A formatter for a workspace's money: `moneyFormatter(ws.settings)(12_345)` -> `$123.45`. */
export function moneyFormatter(settings: MoneySettings): (cents: number) => string {
  const { currency, locale } = moneySettings(settings);
  const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency });
  return (cents) => fmt.format(cents / 100);
}

/** Stripe wants ISO currency codes in lowercase (`cad`). */
export function stripeCurrency(currency: string | null | undefined): string {
  return (currency?.trim() || DEFAULT_CURRENCY).toLowerCase();
}

// ---------------------------------------------------------------------------
// Contact normalization (dedupe keys)
// ---------------------------------------------------------------------------

export function normalizeEmail(email: string | null | undefined): string | null {
  const v = email?.trim().toLowerCase();
  return v ? v : null;
}

/** Digits only; US numbers lose a leading 1 so `+1 (555) 010-2000` == `555-010-2000`. */
export function normalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/x.*$/i, '').replace(/\D/g, '');
  if (!digits) return null;
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

export function domainFromEmail(email: string | null | undefined): string | null {
  const v = normalizeEmail(email);
  if (!v || !v.includes('@')) return null;
  return v.split('@')[1] ?? null;
}

export function displayName(first: string | null | undefined, last: string | null | undefined, fallback = ''): string {
  const name = [first?.trim(), last?.trim()].filter(Boolean).join(' ');
  return name || fallback;
}

// Public link tokens (hash at rest, constant-time comparison).
export * from './tokens';
// Countries, provinces/states, postal codes, address formatting and Canadian sales tax.
export * from './address';
